import { useEffect, useRef, useState } from 'react';
import { eventService, userService } from '@vesper/shared';

const PING_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const WINDOW_MS = 60 * 60 * 1000; // 1 hour

/**
 * Periodically pings GPS when the user has an event starting within 1 hour.
 * Sends location to POST /users/location every 5 minutes.
 * Returns `isTracking` so the UI can show a subtle indicator.
 */
export function useEventProximityPing(): { isTracking: boolean } {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isTracking, setIsTracking] = useState(false);

  useEffect(() => {
    let cancelled = false;

    function clearPingInterval() {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsTracking(false);
    }

    function sendLocationPing() {
      if (!navigator.geolocation) return;

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          const payload = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          userService.updateLocation(payload).catch(() => { /* ignore */ });
        },
        () => { /* permission denied — silently skip */ },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
      );
    }

    async function checkAndSchedule() {
      if (cancelled) return;

      try {
        const events = await eventService.getMyEvents();
        const now = Date.now();
        const hasUpcoming = events.some((evt) => {
          const start = new Date(evt.startTime).getTime();
          return start > now && start - now <= WINDOW_MS;
        });

        if (hasUpcoming) {
          sendLocationPing();
          setIsTracking(true);
          if (intervalRef.current === null) {
            intervalRef.current = setInterval(sendLocationPing, PING_INTERVAL_MS);
          }
        } else {
          clearPingInterval();
        }
      } catch {
        clearPingInterval();
      }
    }

    checkAndSchedule();
    const checkInterval = setInterval(checkAndSchedule, PING_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearPingInterval();
      clearInterval(checkInterval);
    };
  }, []);

  return { isTracking };
}
